import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Base_URL } from "../../url";
import "../../Styling/hrDashboard.css";
import { useAuth } from "../../Context/AuthContext";
import {ArrowLeft, Download, Search, Calendar, Users, Clock, CheckCircle, XCircle, AlertCircle, Mail, User, Building, MapPin, Navigation, ChevronUp, ChevronDown, Edit3, Save, X} from "lucide-react";

const AttendanceDetails = () => {
  const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

  // ✅ Check if a date is Sunday
  const isSunday = (dateString) => {
    if (!dateString) return false;
    try {
      const date = new Date(dateString);
      return date.getDay() === 0; // 0 = Sunday
    } catch {
      return false;
    }
  };

  const isCurrentMonthRange = (start, end) => {
    if (!start || !end) return false;
    const now = new Date();
    const startDate = new Date(start);
    const endDate = new Date(end);
    return (
      startDate.getMonth() === now.getMonth() &&
      endDate.getMonth() === now.getMonth() &&
      startDate.getFullYear() === endDate.getFullYear() &&
      endDate.getFullYear() === now.getFullYear()
    );
  };

  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [attendanceData, setAttendanceData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [dateFilter, setDateFilter] = useState(getTodayDate());
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(getTodayDate());
  const [useDateRange, setUseDateRange] = useState(false);
  
  // Manual update states
  const [manualDate, setManualDate] = useState(getTodayDate());
  const [manualPunchInTime, setManualPunchInTime] = useState("");
  const [manualPunchOutTime, setManualPunchOutTime] = useState("");
  
  const [stats, setStats] = useState({
    total: 0,
    present: 0,
    absent: 0,
    late: 0,
    halfDay: 0,
    active: 0,
    weekoff: 0
  });
  
  // Modal states
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [manualStatus, setManualStatus] = useState("");
  const [updateMethod, setUpdateMethod] = useState("manual"); // "manual" or "quick"

  // ✅ Format time for display
  const formatTime = (timeString) => {
    if (!timeString || timeString === "N/A") return "N/A";
    return timeString;
  };
 
  // ✅ Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // ✅ Format coordinates for display
  const formatCoordinates = (coord) => {
    if (!coord || coord === "N/A") return "N/A";
    
    try {
      const coordValue = parseFloat(coord);
      return isNaN(coordValue) ? coord : coordValue.toFixed(6);
    } catch {
      return coord;
    }
  };

  // ✅ Get status badge - UPDATED WITH WEEKOFF
  const getStatusBadge = (record) => {
    // First check if it's Sunday - automatically show as Weekoff
    if (isSunday(record.attendanceDate)) {
      return { 
        text: "Weekoff", 
        color: "#8b5cf6", 
        icon: <Calendar size={14} /> 
      };
    }
    
    const status = record.attendanceStatus || "Absent";
    
    switch (status.toLowerCase()) {
      case 'present':
        return { 
          text: "Present", 
          color: "#10b981", 
          icon: <CheckCircle size={14} /> 
        };
      
      case 'half day':
        return { text: "Half Day", color: "#f59e0b", icon: <Clock size={14} /> };
      
      case 'absent':
        return { text: "Absent", color: "#ef4444", icon: <XCircle size={14} /> };
      
      case 'active':
        return { text: "Active", color: "#3b82f6", icon: <Clock size={14} /> };
      
      case 'weekoff':
        return { text: "Weekoff", color: "#8b5cf6", icon: <Calendar size={14} /> };
      
      default:
        return { text: status, color: "#6b7280", icon: <AlertCircle size={14} /> };
    }
  };

  // ✅ Calculate manual working hours - UPDATED
  const calculateManualWorkingHours = (punchInTime, punchOutTime) => {
    if (!punchInTime || !punchOutTime) return "N/A";
    
    try {
      const [inHours, inMinutes] = punchInTime.split(':').map(Number);
      const [outHours, outMinutes] = punchOutTime.split(':').map(Number);
      
      const totalInMinutes = inHours * 60 + inMinutes;
      const totalOutMinutes = outHours * 60 + outMinutes;
      
      let totalMinutes = totalOutMinutes - totalInMinutes;
      
      // Handle overnight shifts (if punch out is next day)
      if (totalMinutes < 0) {
        totalMinutes += 24 * 60;
      }
      
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      
      return `${hours}h ${minutes}m (${totalMinutes} minutes)`;
    } catch (error) {
      console.error("Error calculating working hours:", error);
      return "N/A";
    }
  };

  // ✅ Calculate manual status based on working hours - UPDATED TO 8 HOURS
  const getManualStatus = (punchInTime, punchOutTime) => {
    if (!punchInTime || !punchOutTime) return "Absent";
    
    try {
      const [inHours, inMinutes] = punchInTime.split(':').map(Number);
      const [outHours, outMinutes] = punchOutTime.split(':').map(Number);
      
      const totalInMinutes = inHours * 60 + inMinutes;
      const totalOutMinutes = outHours * 60 + outMinutes;
      
      let totalMinutes = totalOutMinutes - totalInMinutes;
      
      // Handle overnight shifts
      if (totalMinutes < 0) {
        totalMinutes += 24 * 60;
      }
      
      // 🎯 UPDATED LOGIC: 8 hours = Present, 5-8 hours = Half Day, <5 hours = Absent
      if (totalMinutes >= 480) { // 8 hours = 480 minutes
        return "Present";
      } else if (totalMinutes >= 300 && totalMinutes < 480) { // 5-8 hours
        return "Half Day";
      } else {
        return "Absent";
      }
    } catch (error) {
      console.error("Error calculating manual status:", error);
      return "Absent";
    }
  };

  // ✅ Check if manual punch in is late - UPDATED TO 9:45 AM
  const getIsLateArrival = (punchInTime) => {
    if (!punchInTime) return false;
    
    try {
      const [hours, minutes] = punchInTime.split(':').map(Number);
      
      // 🎯 UPDATED LOGIC: Late if after 9:45 AM
      if (hours > 9) return true;
      if (hours === 9 && minutes > 45) return true;
      
      return false;
    } catch (error) {
      console.error("Error checking late arrival:", error);
      return false;
    }
  };

  // ✅ Open location in maps
  const openInMaps = (latitude, longitude, address) => {
    if (isValidCoordinate(latitude) && isValidCoordinate(longitude)) {
      try {
        const lat = parseFloat(latitude);
        const lng = parseFloat(longitude);
        if (!isNaN(lat) && !isNaN(lng)) {
          const url = `https://www.google.com/maps?q=${lat},${lng}`;
          window.open(url, '_blank');
          return;
        }
      } catch (error) {
        console.error("Error parsing coordinates:", error);
      }
    }
    
    if (address && address !== "N/A") {
      const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
      window.open(url, '_blank');
    }
  };

  // ✅ Validate coordinates
  const isValidCoordinate = (coord) => {
    if (!coord || coord === "N/A") return false;
    try {
      const value = parseFloat(coord);
      return !isNaN(value) && value >= -90 && value <= 90;
    } catch {
      return false;
    }
  };

  // ✅ Handle column sorting
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    
    useEffect(() => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);
      
      return () => {
        clearTimeout(handler);
      };
    }, [value, delay]);
    
    return debouncedValue;
  };

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // ✅ Sort data based on configuration
  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      let aValue = a[sortConfig.key];
      let bValue = b[sortConfig.key];

      switch (sortConfig.key) {
        case 'user_ID':
          aValue = parseInt(aValue) || 0;
          bValue = parseInt(bValue) || 0;
          break;
        case 'username':
        case 'role':
        case 'address':
        case 'punchOutAddress':
        case 'city':
        case 'punchOutCity':
          aValue = (aValue || '').toString().toLowerCase();
          bValue = (bValue || '').toString().toLowerCase();
          break;
        case 'attendanceDate':
          aValue = aValue ? new Date(aValue).getTime() : 0;
          bValue = bValue ? new Date(bValue).getTime() : 0;
          break;
        case 'punchIn_Time':
        case 'punchOut_Time':
          aValue = aValue || '99:99';
          bValue = bValue || '99:99';
          break;
        case 'workingMinutes':
          aValue = aValue || 0;
          bValue = bValue || 0;
          break;
        case 'attendanceStatus':
          aValue = (aValue || '').toString().toLowerCase();
          bValue = (bValue || '').toString().toLowerCase();
          break;
        case 'latitude':
        case 'longitude':
        case 'punchOutLatitude':
        case 'punchOutLongitude':
          aValue = aValue === 'N/A' ? 999 : parseFloat(aValue) || 0;
          bValue = bValue === 'N/A' ? 999 : parseFloat(bValue) || 0;
          break;
        default:
          aValue = aValue || '';
          bValue = bValue || '';
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  // ✅ Get sort icon for column header
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return (
        <div className="sort-icons">
          <ChevronUp size={12} className="sort-icon" />
          <ChevronDown size={12} className="sort-icon" />
        </div>
      );
    }
    
    return sortConfig.direction === 'asc' ? 
      <ChevronUp size={14} className="sort-icon active" /> : 
      <ChevronDown size={14} className="sort-icon active" />;
  };

  // ✅ Fetch Attendance Data from New API - UPDATED FOR DATE RANGE
  const fetchAttendanceData = useCallback(async (searchQuery = "") => {
    if (!user?.organisationId) {
      setError("No organization selected");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError("");

    try {
      let url = '';
      
      if (useDateRange && startDate && endDate) {
        const params = new URLSearchParams({
          organisationId: user.organisationId,
          startDate: startDate,
          endDate: endDate
        });
        
        if (searchQuery && searchQuery.trim() !== '') {
          params.append('searchTerm', searchQuery.trim());
        }
        
        url = `${Base_URL}/AttendanceReport/GetAttendanceSummaryByDateRange?${params.toString()}`;
      } else {
        url = `${Base_URL}/AttendanceReport/GetAttendanceSummary?organisationId=${user.organisationId}&date=${dateFilter}`;
      }
      
      console.log("🔍 Fetching attendance from:", url);

      const response = await fetch(url);
      
      if (!response.ok) {
        if (response.status === 404) {
          setAttendanceData([]);
          setFilteredData([]);
          setError("No attendance data found for the selected criteria");
          return;
        }
        throw new Error(`Failed to fetch attendance data: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log("📊 API Response:", data);
      
      if (data.summary) {
        setStats(data.summary);
      }
      
      const attendanceArray = Array.isArray(data.attendanceData) ? data.attendanceData : [];
      
      // ✅ PROCESS DATA: Auto-mark Sundays as Weekoff
      const processedData = attendanceArray.map(record => {
        const isSundayRecord = isSunday(record.attendanceDate);
        
        return {
          ...record,
          latitude: record.latitude || "N/A",
          longitude: record.longitude || "N/A",
          address: record.address || "N/A",
          city: record.city || "N/A",
          punchOutLatitude: record.punchOutLatitude || "N/A",
          punchOutLongitude: record.punchOutLongitude || "N/A",
          punchOutAddress: record.punchOutAddress || "N/A",
          punchOutCity: record.punchOutCity || "N/A",
          // Auto-mark as Weekoff if Sunday
          attendanceStatus: isSundayRecord ? "Weekoff" : (record.attendanceStatus || "Absent")
        };
      });
      
      setAttendanceData(processedData);
      setFilteredData(processedData);
      
    } catch (err) {
      console.error("❌ Error fetching attendance data:", err);
      setError(err.message || "Failed to fetch attendance data");
      setAttendanceData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  }, [user?.organisationId, dateFilter, useDateRange, startDate, endDate]);

  // ✅ Apply filters and sorting - UPDATED FOR FRONTEND FILTERING
  useEffect(() => {
    console.log("🔄 Applying frontend filters...");
    
    let filtered = [...attendanceData];

    // Apply search filter on frontend (instant results)
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(record =>
        (record.username?.toLowerCase() || '').includes(searchLower) ||
        (record.email?.toLowerCase() || '').includes(searchLower) ||
        (record.role?.toLowerCase() || '').includes(searchLower) ||
        (record.user_ID?.toString() || '').includes(searchTerm)
      );
    }

    // Apply status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(record => {
        const status = record.attendanceStatus?.toLowerCase() || 'absent';
        
        switch (statusFilter.toLowerCase()) {
          case 'present':
            return status === 'present';
          case 'late':
            return record.isLateArrival;
          case 'halfday':
            return status === 'half day';
          case 'absent':
            return status === 'absent';
          case 'active':
            return status === 'active';
          case 'weekoff':
            return status === 'weekoff';
          default:
            return true;
        }
      });
    }

    // Apply sorting
    const sortedData = getSortedData(filtered);
    setFilteredData(sortedData);
  }, [attendanceData, searchTerm, statusFilter, sortConfig]);

  // ✅ Fetch data when date range changes OR when debounced search term changes
  useEffect(() => {
    if (user?.organisationId) {
      fetchAttendanceData(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, dateFilter, useDateRange, startDate, endDate, user?.organisationId]);

  // ✅ Handle stat card click - filter by status
  const handleStatCardClick = (status) => {
    if (status === "all") {
      setStatusFilter("all");
      setFilteredData(attendanceData);
    } else {
      setStatusFilter(status);
      const filtered = attendanceData.filter(record => {
        if (status === "late") {
          return record.isLateArrival;
        }
        return record.attendanceStatus?.toLowerCase() === status.toLowerCase();
      });
      setFilteredData(filtered);
    }
  };

  // ✅ Calculate working hours from minutes
  const calculateWorkingHours = (minutes) => {
    if (!minutes || minutes === 0) return { hours: 0, minutes: 0 };
    return {
      hours: Math.floor(minutes / 60),
      minutes: minutes % 60
    };
  };

  // ✅ Mark Employee with Manual Times
  const markEmployeeWithManualTimes = async (employee, status, date, punchInTime, punchOutTime) => {
    if (!employee || !employee.user_ID || !punchInTime || !punchOutTime) {
      setError("Please fill all required fields: date, punch in time, and punch out time");
      return;
    }

    // Validate that punch out time is after punch in time
    const [inHours, inMinutes] = punchInTime.split(':').map(Number);
    const [outHours, outMinutes] = punchOutTime.split(':').map(Number);
    
    const totalInMinutes = inHours * 60 + inMinutes;
    const totalOutMinutes = outHours * 60 + outMinutes;
    
    if (totalOutMinutes <= totalInMinutes) {
      setError("Punch out time must be after punch in time");
      return;
    }

    setIsUpdating(true);
    try {
      const url = `${Base_URL}/AttendanceReport/UpdateAttendanceStatus`;
      
      const formattedPunchInTime = `${punchInTime}:00`;
      const formattedPunchOutTime = `${punchOutTime}:00`;
      
      const requestBody = {
        userId: employee.user_ID,
        organisationId: user.organisationId,
        date: date,
        status: status,
        punchInTime: formattedPunchInTime,
        punchOutTime: formattedPunchOutTime
      };

      console.log("Sending manual attendance update:", requestBody);

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to update attendance: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      console.log("Manual attendance update success:", result);

      await fetchAttendanceData();
      closeEmployeeModal();
      
      setError(`✅ Successfully updated ${employee.username}'s attendance for ${formatDate(date)} as ${status}`);
      setTimeout(() => setError(""), 5000);
      
    } catch (err) {
      console.error("Error updating employee attendance manually:", err);
      setError(err.message || "Failed to update attendance. Please try again.");
    } finally {
      setIsUpdating(false);
    }
  };

  // ✅ Mark Employee with Manual Status (HR Override)
  const markEmployeeWithStatus = async (employee, status) => {
    if (!employee || !employee.user_ID) {
      setError("Invalid employee data");
      return;
    }

    setIsUpdating(true);
    try {
      let url = '';
      let requestBody = {};

      if (status === 'Present' && employee.attendanceStatus === 'Active') {
        url = `${Base_URL}/AttendanceReport/MarkAsPresent`;
        requestBody = {
          userId: employee.user_ID,
          organisationId: user.organisationId,
          date: dateFilter,
          punchOutTime: new Date().toTimeString().split(' ')[0]
        };
      } else {
        url = `${Base_URL}/AttendanceReport/UpdateAttendanceStatus`;
        requestBody = {
          userId: employee.user_ID,
          organisationId: user.organisationId,
          date: dateFilter,
          status: status,
          workingHours: employee.workingMinutes || 0
        };
      }

      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`Failed to update attendance: ${response.status}`);
      }

      const result = await response.json();
      console.log("Update attendance success:", result);

      await fetchAttendanceData();
      closeEmployeeModal();
      
      setError(`Successfully marked ${employee.username} as ${status}`);
      setTimeout(() => setError(""), 3000);
      
    } catch (err) {
      console.error("Error updating employee attendance:", err);
      setError(err.message || "Failed to update attendance");
    } finally {
      setIsUpdating(false);
    }
  };

  // ✅ Open Employee Detail Modal
const openEmployeeModal = (employee, specificDate = null) => {
  setSelectedEmployee(employee);
  setManualStatus("");
  
  // ✅ FIX: Priority order for date selection:
  // 1. Specific date passed (from filtered context)
  // 2. Employee's attendance date from the record
  // 3. Today's date as fallback
  const modalDate = specificDate || employee.attendanceDate || getTodayDate();
  setManualDate(modalDate);
  
  // ✅ Pre-fill existing times for better UX
  setManualPunchInTime(employee.punchIn_Time || "");
  setManualPunchOutTime(employee.punchOut_Time || "");
  
  setManualStatus(employee.attendanceStatus || "");
  setUpdateMethod("manual");
  setShowEmployeeModal(true);
};

  // ✅ Close Employee Detail Modal
  const closeEmployeeModal = () => {
    setSelectedEmployee(null);
    setManualStatus("");
    setManualDate(getTodayDate());
    setManualPunchInTime("");
    setManualPunchOutTime("");
    setUpdateMethod("manual");
    setShowEmployeeModal(false);
  };

  // ✅ Export to CSV
  const exportToCSV = () => {
    const headers = [
      "Employee ID", "Name", "Email", "Role", "Date", 
      "Punch In", "Punch Out", "Working Hours", "Status", "Late Arrival",
      "Punch In Latitude", "Punch In Longitude", "Punch In Address", "Punch In City",
      "Punch Out Latitude", "Punch Out Longitude", "Punch Out Address", "Punch Out City"
    ];
    
    const csvData = filteredData.map(record => {
      const status = getStatusBadge(record);
      return [
        record.user_ID || "N/A",
        record.username || "N/A",
        record.email || "N/A",
        record.role || "N/A",
        formatDate(record.attendanceDate),
        formatTime(record.punchIn_Time),
        formatTime(record.punchOut_Time),
        record.workingHoursDisplay || "N/A",
        status.text,
        record.isLateArrival ? "Yes" : "No",
        record.latitude || "N/A",
        record.longitude || "N/A",
        record.address || "N/A",
        record.city || "N/A",
        record.punchOutLatitude || "N/A",
        record.punchOutLongitude || "N/A",
        record.punchOutAddress || "N/A",
        record.punchOutCity || "N/A"
      ];
    });

    const csvContent = [
      headers.join(","),
      ...csvData.map(row => row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(","))
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `attendance-${user?.organisationId || "data"}-${dateFilter}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // ✅ Handle date range toggle
  const handleDateRangeToggle = () => {
    setUseDateRange(!useDateRange);
    if (!useDateRange) {
      const today = getTodayDate();
      setStartDate(today);
      setEndDate(today);
    } else {
      setStartDate(null);
      setEndDate(getTodayDate());
    }
  };

  // ✅ Get display date range text
  const getDateRangeDisplay = () => {
    if (useDateRange && startDate && endDate) {
      return `${formatDate(startDate)} to ${formatDate(endDate)}`;
    }
    return formatDate(dateFilter);
  };

  // Initial data fetch
  useEffect(() => {
    if (user?.organisationId) {
      fetchAttendanceData();
    } else {
      setError("No organization data provided");
      setLoading(false);
    }
  }, [fetchAttendanceData, user?.organisationId]);

  // Refresh data when date or date range changes
  useEffect(() => {
    if (user?.organisationId) {
      fetchAttendanceData();
    }
  }, [dateFilter, useDateRange, startDate, endDate]);

  if (loading) {
    return (
      <div className="attendance-details-container">
        <div className="attendance-loading">
          <div className="loading-spinner"></div>
          <p>Loading attendance data...</p>
        </div>
      </div>
    );
  }

return (
  <div className="attendance-details-container">
    {/* Employee Detail Modal */}
    {showEmployeeModal && selectedEmployee && (
      <div className="modal-overlay">
        <div className="modal-content employee-detail-modal">
          <div className="modal-header">
            <div className="modal-title-section">
              <h2>Employee Attendance Management</h2>
              <p>Manually update attendance for {selectedEmployee.username}</p>
            </div>
            <button className="close-button" onClick={closeEmployeeModal}>
              <X size={20} />
            </button>
          </div>

          <div className="modal-body">
            {/* Employee Info Card */}
            <div className="employee-info-card">
              <div className="employee-avatar">
                <User size={32} />
              </div>
              <div className="employee-details">
                <h3>{selectedEmployee.username}</h3>
                <p className="employee-email">{selectedEmployee.email}</p>
                <p className="employee-role">Role: {selectedEmployee.role || "Not specified"}</p>
                <p className="employee-id">ID: #{selectedEmployee.user_ID}</p>
              </div>
            </div>

            {/* Current Attendance Details */}
            <div className="current-attendance-section">
              <h4>Current Attendance Details</h4>
              <div className="attendance-details-grid">
                <div className="detail-item">
                  <label>Date</label>
                  <span>{formatDate(selectedEmployee.attendanceDate)}</span>
                </div>
                {/* ... other current attendance details ... */}
              </div>
            </div>

            {/* Manual Attendance Update Section */}
            <div className="manual-update-section">
              <h4>Manual Attendance Update</h4>
              <div className="update-description">
                <p>Use this section to manually update attendance for employees who forgot to punch in/out. Select the date and enter the times below.</p>
              </div>
              
              <div className="manual-update-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Select Date *</label>
                    <input
                      type="date"
                      value={manualDate}
                      onChange={(e) => setManualDate(e.target.value)}
                      max={getTodayDate()}
                      className="form-input"
                      // ✅ FIX: Lock date to the selected record's date
                      disabled={true}
                    />
                    <small className="date-locked-note">
                      📅 Date locked to {formatDate(selectedEmployee.attendanceDate)} - You are modifying this specific date's record
                    </small>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Punch In Time *</label>
                    <input
                      type="time"
                      value={manualPunchInTime}
                      onChange={(e) => setManualPunchInTime(e.target.value)}
                      className="form-input"
                    />
                    <small>
                      Enter the actual punch in time for {formatDate(selectedEmployee.attendanceDate)} (HH:MM)
                    </small>
                  </div>

                  <div className="form-group">
                    <label>Punch Out Time *</label>
                    <input
                      type="time"
                      value={manualPunchOutTime}
                      onChange={(e) => setManualPunchOutTime(e.target.value)}
                      className="form-input"
                    />
                    <small>
                      Enter the actual punch out time for {formatDate(selectedEmployee.attendanceDate)} (HH:MM)
                    </small>
                  </div>
                </div>

                {/* Auto-calculated status preview */}
                {manualPunchInTime && manualPunchOutTime && (
                  <div className="status-preview">
                    <h5>Status Preview</h5>
                    <div className="preview-details">
                      <p><strong>Working Hours:</strong> {calculateManualWorkingHours(manualPunchInTime, manualPunchOutTime)}</p>
                      <p><strong>Calculated Status:</strong> 
                        <span className={`suggested-status ${getManualStatus(manualPunchInTime, manualPunchOutTime).toLowerCase().replace(' ', '-')}`}>
                          {getManualStatus(manualPunchInTime, manualPunchOutTime)}
                        </span>
                      </p>
                      {getIsLateArrival(manualPunchInTime) && (
                        <p><strong>Late Arrival:</strong> <span className="late-indicator">Yes</span></p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* HR Actions */}
            <div className="hr-actions-section">
              <h4>Update Attendance</h4>
              
              <div className="status-selection">
                <label>Select Status to Apply:</label>
                <div className="status-buttons">
                  <button 
                    className={`status-btn present ${manualStatus === 'Present' ? 'active' : ''}`}
                    onClick={() => setManualStatus('Present')}
                    disabled={!manualPunchInTime || !manualPunchOutTime}
                  >
                    <CheckCircle size={16} />
                    Present
                  </button>
                  <button 
                    className={`status-btn half-day ${manualStatus === 'Half Day' ? 'active' : ''}`}
                    onClick={() => setManualStatus('Half Day')}
                    disabled={!manualPunchInTime || !manualPunchOutTime}
                  >
                    <Clock size={16} />
                    Half Day
                  </button>
                  <button 
                    className={`status-btn absent ${manualStatus === 'Absent' ? 'active' : ''}`}
                    onClick={() => setManualStatus('Absent')}
                    disabled={!manualPunchInTime || !manualPunchOutTime}
                  >
                    <XCircle size={16} />
                    Absent
                  </button>
                </div>
              </div>

              <div className="action-buttons">
                <button 
                  className="mark-status-btn"
                  onClick={() => markEmployeeWithManualTimes(selectedEmployee, manualStatus, manualDate, manualPunchInTime, manualPunchOutTime)}
                  disabled={isUpdating || !manualStatus || !manualPunchInTime || !manualPunchOutTime}
                >
                  {isUpdating ? (
                    <>
                      <div className="loading-spinner-small"></div>
                      Updating...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      Update Attendance for {formatDate(selectedEmployee.attendanceDate)} as {manualStatus}
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Status Guide */}
            <div className="status-guide-section">
              <h5>Status Guidelines</h5>
              <div className="status-guide">
                <p><strong>• Present:</strong> 8+ hours (480+ minutes)</p>
                <p><strong>• Half Day:</strong> 5-8 hours (300-479 minutes)</p>
                <p><strong>• Absent:</strong> Less than 5 hours or no attendance</p>
                <p><strong>• Late Arrival:</strong> Punch in after 9:45 AM</p>
              </div>
            </div>
          </div>

          <div className="modal-actions">
            <button className="btn-secondary" onClick={closeEmployeeModal}>
              Close
            </button>
          </div>
        </div>
      </div>
    )}
      <div className="attendance-header">
        <div className="attendance-header-main">
          <button 
            className="back-button"
            onClick={() => navigate('/hr-dashboard')}
          >
            <ArrowLeft size={20} />
            Back to HR Dashboard
          </button>
          <div className="header-title-section">
            <h1>Attendance Management</h1>
            <p>Detailed attendance records for Organization {user?.organisationId} - {getDateRangeDisplay()}</p>
          </div>
        </div>
        
        <div className="header-actions">
          <button 
            className="view-leaves-btn"
            onClick={() => navigate('/view-leaves')}
          >
            <Users size={16} />
            View Leaves
          </button>
          <button 
            className="export-btn" 
            onClick={exportToCSV}     
            disabled={filteredData.length === 0}
          >
            <Download size={16} />
            Export CSV
          </button>
        </div>
      </div>

      {/* Stats Cards - CLICKABLE */}
      <div className="attendance-stats-grid">
        <div 
          className={`stat-card total ${statusFilter === "all" ? "active" : ""}`}
          onClick={() => handleStatCardClick("all")}
          style={{ cursor: "pointer" }}
        >
          <div className="stat-icon">
            <Users size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{stats.total}</div>
            <div className="stat-label">Total Employees</div>
          </div>
        </div>
        
        <div 
          className={`stat-card present ${statusFilter === "present" ? "active" : ""}`}
          onClick={() => handleStatCardClick("present")}
          style={{ cursor: "pointer" }}
        >
          <div className="stat-icon">
            <CheckCircle size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{stats.present}</div>
            <div className="stat-label">Present Today</div>
            <div className="stat-subtext">8+ hours</div>
          </div>
        </div>
        
        <div 
          className={`stat-card absent ${statusFilter === "absent" ? "active" : ""}`}
          onClick={() => handleStatCardClick("absent")}
          style={{ cursor: "pointer" }}
        >
          <div className="stat-icon">
            <XCircle size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{stats.absent}</div>
            <div className="stat-label">Absent</div>
            <div className="stat-subtext">No attendance</div>
          </div>
        </div>
        
        <div 
          className={`stat-card late ${statusFilter === "late" ? "active" : ""}`}
          onClick={() => handleStatCardClick("late")}
          style={{ cursor: "pointer" }}
        >
          <div className="stat-icon">
            <Clock size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{stats.late}</div>
            <div className="stat-label">Late Arrivals</div>
            <div className="stat-subtext">After 9:45 AM</div>
          </div>
        </div>

        <div 
          className={`stat-card half-day ${statusFilter === "halfday" ? "active" : ""}`}
          onClick={() => handleStatCardClick("halfday")}
          style={{ cursor: "pointer" }}
        >
          <div className="stat-icon">
            <Clock size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{stats.halfDay}</div>
            <div className="stat-label">Half Day</div>
            <div className="stat-subtext">5-8 hours</div>
          </div>
        </div>

        <div 
          className={`stat-card active-status ${statusFilter === "active" ? "active" : ""}`}
          onClick={() => handleStatCardClick("active")}
          style={{ cursor: "pointer" }}
        >
          <div className="stat-icon">
            <Clock size={24} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{stats.active}</div>
            <div className="stat-label">Currently Active</div>
            <div className="stat-subtext">Working now</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="attendance-filters">
        <div className="filter-group">
          <div className="search-box">
            <Search size={18} />
            <input
              type="text"
              placeholder="Search by name, email, role, employee ID, address, or coordinates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        
        <div className="filter-group">
          <select 
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="present">Present</option>
            <option value="late">Late</option>
            <option value="halfday">Half Day</option>
            <option value="absent">Absent</option>
            <option value="active">Active</option>
          </select>
        </div>
        
        {/* Date Range Toggle */}
        <div className="filter-group">
          <label className="toggle-label">
            <input
              type="checkbox"
              checked={useDateRange}
              onChange={handleDateRangeToggle}
              className="toggle-input"
            />
            <span className="toggle-slider"></span>
            Date Range
          </label>
        </div>

        {/* Date Selection - Dynamic based on toggle */}
        {useDateRange ? (
          <>
            <div className="filter-group">
              <div className="date-filter-wrapper">
                <Calendar size={16} className="date-filter-icon" />
                <input
                  className="filter-input date-input"
                  type="date"
                  value={startDate || ""}
                  onChange={e => setStartDate(e.target.value)}
                  max={getTodayDate()}
                  title="Start date"
                />
              </div>
            </div>
            <div className="filter-group">
              <div className="date-filter-wrapper">
                <Calendar size={16} className="date-filter-icon" />
                <input
                  className="filter-input date-input"
                  type="date"
                  value={endDate}
                  onChange={e => setEndDate(e.target.value)}
                  min={startDate || ""}
                  max={getTodayDate()}
                  title="End date"
                />
              </div>
            </div>
          </>
        ) : (
          <div className="filter-group">
            <div className="date-filter-wrapper">
              <Calendar size={16} className="date-filter-icon" />
              <input
                className="filter-input date-input"
                type="date"
                value={dateFilter}
                onChange={e => setDateFilter(e.target.value)}
                max={getTodayDate()}
                title="Filter by date"
              />
            </div>
          </div>
        )}
        
        <div className="filter-group">
          <button 
            className="clear-filters"
            onClick={() => {
              setSearchTerm("");
              setStatusFilter("all");
              setStartDate(null);
              setEndDate(getTodayDate());
              setDateFilter(getTodayDate());
              setUseDateRange(false);
              setSortConfig({ key: null, direction: 'asc' });
            }}
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Results Info */}
      <div className="results-info">
        <span>Showing {filteredData.length} of {attendanceData.length} records</span>
        {searchTerm && (
          <span className="search-term">for "{searchTerm}"</span>
        )}
        {statusFilter !== "all" && (
          <span className="status-filter">with status: {statusFilter}</span>
        )}
        {sortConfig.key && (
          <span className="sort-info">
            Sorted by {sortConfig.key} ({sortConfig.direction === 'asc' ? 'A-Z' : 'Z-A'})
          </span>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          <AlertCircle size={18} />
          {error}
        </div>
      )}

      {/* Attendance Table */}
      <div className="attendance-table-container">
        {filteredData.length > 0 ? (
          <div className="table-scroll-wrapper">
            <table className="attendance-table">
              <thead>
                <tr>
                  <th className="sortable-header" onClick={() => handleSort('user_ID')}>
                    <div className="header-content">
                      Employee ID
                      {getSortIcon('user_ID')}
                    </div>
                  </th>
                  <th className="sortable-header" onClick={() => handleSort('username')}>
                    <div className="header-content">
                      Employee Name
                      {getSortIcon('username')}
                    </div>
                  </th>
                  <th className="sortable-header" onClick={() => handleSort('role')}>
                    <div className="header-content">
                      Role
                      {getSortIcon('role')}
                    </div>
                  </th>
                  <th className="sortable-header" onClick={() => handleSort('attendanceDate')}>
                    <div className="header-content">
                      Date
                      {getSortIcon('attendanceDate')}
                    </div>
                  </th>
                  <th className="sortable-header" onClick={() => handleSort('punchIn_Time')}>
                    <div className="header-content">
                      Punch In
                      {getSortIcon('punchIn_Time')}
                    </div>
                  </th>
                  <th className="sortable-header" onClick={() => handleSort('punchOut_Time')}>
                    <div className="header-content">
                      Punch Out
                      {getSortIcon('punchOut_Time')}
                    </div>
                  </th>
                  <th className="sortable-header" onClick={() => handleSort('workingMinutes')}>
                    <div className="header-content">
                      Working Hours
                      {getSortIcon('workingMinutes')}
                    </div>
                  </th>
                  <th>Status</th>
                  <th>Late</th>
                  
                  {/* Punch In Location - SEPARATE COLUMNS */}
                  <th className="sortable-header" onClick={() => handleSort('latitude')}>
                    <div className="header-content">
                      Punch In Latitude
                      {getSortIcon('latitude')}
                    </div>
                  </th>
                  <th className="sortable-header" onClick={() => handleSort('longitude')}>
                    <div className="header-content">
                      Punch In Longitude
                      {getSortIcon('longitude')}
                    </div>
                  </th>
                  <th className="sortable-header" onClick={() => handleSort('address')}>
                    <div className="header-content">
                      Punch In Address
                      {getSortIcon('address')}
                    </div>
                  </th>
                  <th className="sortable-header" onClick={() => handleSort('city')}>
                    <div className="header-content">
                      Punch In City
                      {getSortIcon('city')}
                    </div>
                  </th>
                  
                  {/* Punch Out Location - SEPARATE COLUMNS */}
                  <th className="sortable-header" onClick={() => handleSort('punchOutLatitude')}>
                    <div className="header-content">
                      Punch Out Latitude
                      {getSortIcon('punchOutLatitude')}
                    </div>
                  </th>
                  <th className="sortable-header" onClick={() => handleSort('punchOutLongitude')}>
                    <div className="header-content">
                      Punch Out Longitude
                      {getSortIcon('punchOutLongitude')}
                    </div>
                  </th>
                  <th className="sortable-header" onClick={() => handleSort('punchOutAddress')}>
                    <div className="header-content">
                      Punch Out Address
                      {getSortIcon('punchOutAddress')}
                    </div>
                  </th>
                  <th className="sortable-header" onClick={() => handleSort('punchOutCity')}>
                    <div className="header-content">
                      Punch Out City
                      {getSortIcon('punchOutCity')}
                    </div>
                  </th>
                </tr>
              </thead>
           <tbody>
  {filteredData.map((record, index) => {
    const status = getStatusBadge(record);
    const hasValidPunchInCoordinates = isValidCoordinate(record.latitude) && isValidCoordinate(record.longitude);
    const hasValidPunchOutCoordinates = isValidCoordinate(record.punchOutLatitude) && isValidCoordinate(record.punchOutLongitude);
    const hasPunchInAddress = record.address && record.address !== "N/A";
    const hasPunchOutAddress = record.punchOutAddress && record.punchOutAddress !== "N/A";
    const isPunchInClickable = hasValidPunchInCoordinates || hasPunchInAddress;
    const isPunchOutClickable = hasValidPunchOutCoordinates || hasPunchOutAddress;
    
    // ✅ FIX: Create unique key combining user_ID, date, and index
    const uniqueKey = `${record.user_ID}-${record.attendanceDate}-${index}`;
    
    return (
      <tr key={uniqueKey} className="attendance-row">
        <td>
          <div className="employee-id-cell">
            <div className="employee-id-badge">#{record.user_ID}</div>
          </div>
        </td>
        <td>
          <div 
            className="employee-info clickable"
           onClick={() => openEmployeeModal(record, record.attendanceDate)}
            style={{ cursor: "pointer" }}
          >
            <div className="avatar">
              <User size={16} />
            </div>
            <div className="employee-details">
              <div className="employee-name">{record.username || "N/A"}</div>
              <div className="employee-email">{record.email || "N/A"}</div>
            </div>
          </div>
        </td>
        <td>
          <span className="role-badge">{record.role || "N/A"}</span>
        </td>
        <td>
          {formatDate(record.attendanceDate)}
        </td>
        <td>
          <div className="time-cell">
            <Clock size={14} />
            {formatTime(record.punchIn_Time)}
          </div>
        </td>
        <td>
          <div className="time-cell">
            <Clock size={14} />
            {formatTime(record.punchOut_Time)}
          </div>
        </td>
        <td>
          <div className="working-hours">
            {record.workingHoursDisplay || "N/A"}
          </div>
        </td>
        <td>
          <div 
            className="Attendance-status-badge"
            style={{ 
              backgroundColor: `${status.color}15`, 
              color: status.color, 
              borderColor: status.color 
            }}
          >
            {status.icon}
            {status.text}
          </div>
        </td>
        <td>
          <div className="late-indicator">
            {record.isLateArrival ? (
              <span className="late-badge">Late</span>
            ) : (
              <span className="on-time">On Time</span>
            )}
          </div>
        </td>
        
        {/* Punch In Location - SEPARATE COLUMNS */}
        <td>
          <div 
            className={`coordinate-cell latitude ${isPunchInClickable ? 'clickable' : 'not-clickable'}`}
            onClick={isPunchInClickable ? () => openInMaps(record.latitude, record.longitude, record.address) : undefined}
            title={isPunchInClickable ? "Click to open Punch In location in Google Maps" : "No Punch In location data available"}
          >
            <MapPin size={12} />
            {formatCoordinates(record.latitude)}
          </div>
        </td>
        <td>
          <div 
            className={`coordinate-cell longitude ${isPunchInClickable ? 'clickable' : 'not-clickable'}`}
            onClick={isPunchInClickable ? () => openInMaps(record.latitude, record.longitude, record.address) : undefined}
            title={isPunchInClickable ? "Click to open Punch In location in Google Maps" : "No Punch In location data available"}
          >
            <MapPin size={12} />
            {formatCoordinates(record.longitude)}
          </div>
        </td>
        <td>
          <div 
            className={`address-cell ${isPunchInClickable ? 'clickable' : 'not-clickable'}`}
            onClick={isPunchInClickable ? () => openInMaps(record.latitude, record.longitude, record.address) : undefined}
            title={isPunchInClickable ? "Click to open Punch In location in Google Maps" : "No Punch In location data available"}
          >
            <Navigation size={12} />
            <span className="address-text">{record.address || "N/A"}</span>
          </div>
        </td>
        <td>
          <div className="city-cell">
            <span className="city-text">{record.city || "N/A"}</span>
          </div>
        </td>
        
        {/* Punch Out Location - SEPARATE COLUMNS */}
        <td>
          <div 
            className={`coordinate-cell latitude ${isPunchOutClickable ? 'clickable' : 'not-clickable'}`}
            onClick={isPunchOutClickable ? () => openInMaps(record.punchOutLatitude, record.punchOutLongitude, record.punchOutAddress) : undefined}
            title={isPunchOutClickable ? "Click to open Punch Out location in Google Maps" : "No Punch Out location data available"}
          >
            <MapPin size={12} />
            {formatCoordinates(record.punchOutLatitude)}
          </div>
        </td>
        <td>
          <div 
            className={`coordinate-cell longitude ${isPunchOutClickable ? 'clickable' : 'not-clickable'}`}
            onClick={isPunchOutClickable ? () => openInMaps(record.punchOutLatitude, record.punchOutLongitude, record.punchOutAddress) : undefined}
            title={isPunchOutClickable ? "Click to open Punch Out location in Google Maps" : "No Punch Out location data available"}
          >
            <MapPin size={12} />
            {formatCoordinates(record.punchOutLongitude)}
          </div>
        </td>
        <td>
          <div 
            className={`address-cell ${isPunchOutClickable ? 'clickable' : 'not-clickable'}`}
            onClick={isPunchOutClickable ? () => openInMaps(record.punchOutLatitude, record.punchOutLongitude, record.punchOutAddress) : undefined}
            title={isPunchOutClickable ? "Click to open Punch Out location in Google Maps" : "No Punch Out location data available"}
          >
            <Navigation size={12} />
            <span className="address-text">{record.punchOutAddress || "N/A"}</span>
          </div>
        </td>
        <td>
          <div className="city-cell">
            <span className="city-text">{record.punchOutCity || "N/A"}</span>
          </div>
        </td>
      </tr>
    );
  })}
</tbody>
            </table>
          </div>
        ) : (
          <div className="no-data">
            <Users size={48} />
            <h3>No attendance records found</h3>
            <p>
              {searchTerm || statusFilter !== "all" || dateFilter !== getTodayDate() || (useDateRange && startDate && endDate)
                ? "Try adjusting your filters to see more results"
                : "No attendance data available for the selected date"
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AttendanceDetails;